import { useCallback, useEffect, useRef, useState } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import {
  ArrowPathIcon,
  ChevronDownIcon,
  HeartIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../providers/auth/useAuth'
import {
  AdManagementService,
  type BrandLookupMatch,
  type CompetitorAd,
  type CompetitorView,
  type GeneratedAdResponse,
  type SuggestedCompetitor,
} from '../client'
import AdChatReview from '../components/ads/AdChatReview'
import GeneratedAdFlow from '../components/ads/GeneratedAdFlow'
import CloneAdDialog, { type CopyStarted } from '../components/competitors/CloneAdDialog'
import CompetitorAdCard from '../components/competitors/CompetitorAdCard'
import CompetitorsSelector from '../components/competitors/CompetitorsSelector'
import { apiErrorMessage } from '../lib/api'
import { isBusy, mergeAds, type MediaFilter } from '../lib/competitorAds'
import posthog from '../lib/posthog'

const POLL_INTERVAL = 5000
const RESEARCH_STEPS = [
  { text: 'Looking them up on Facebook…', durationMs: 4000 },
  { text: 'Digging through the Facebook Ads Library…', durationMs: 6000 },
  { text: 'Pulling down their latest creatives…', durationMs: 5000 },
  { text: 'Piecing together the insights…', durationMs: 0 },
]

const MEDIA_LABELS: Record<MediaFilter, string> = {
  all: 'Ad Type: All',
  static: 'Ad Type: Static',
  video: 'Ad Type: Video',
}

const CHIP = 'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition'
const CHIP_IDLE = 'border-gray-300 bg-stone-100 text-gray-700 hover:bg-stone-200'
const CHIP_ON = 'border-purple-600 bg-purple-100 text-purple-600'
const SKELETON = 'animate-pulse rounded-lg bg-slate-200/70'
const SKELETON_CARDS = [260, 340, 280, 380, 300, 320, 240, 360]

function ToolbarSkeleton() {
  return (
    <div className="flex shrink-0 items-center gap-2">
      {['w-28', 'w-32', 'w-28', 'w-48'].map((width, index) => (
        <div key={index} className={`${SKELETON} h-9 ${width}`} />
      ))}
    </div>
  )
}

function GridSkeleton() {
  return (
    <>
      <div className={`${SKELETON} ml-auto h-3 w-32`} />
      <div className="rounded-2xl border border-slate-100 p-3">
        <div className="columns-1 gap-3 sm:columns-2 lg:columns-3 xl:columns-4">
          {SKELETON_CARDS.map((height, index) => (
            <div key={index} style={{ height }} className={`${SKELETON} mb-3 break-inside-avoid`} />
          ))}
        </div>
      </div>
    </>
  )
}

export default function CompetitorResearch() {
  const { brand } = useAuth()
  const brandId = brand?.id
  const [competitors, setCompetitors] = useState<CompetitorView[]>([])
  const [suggested, setSuggested] = useState<SuggestedCompetitor[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [discovering, setDiscovering] = useState(false)
  const [discoveryError, setDiscoveryError] = useState('')
  const [media, setMedia] = useState<MediaFilter>('all')
  const [favouritesOnly, setFavouritesOnly] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [refreshing, setRefreshing] = useState<Set<string>>(new Set())
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [adding, setAdding] = useState('')
  const [cloning, setCloning] = useState<{ ad: CompetitorAd; cardIndex: number } | null>(null)
  const [review, setReview] = useState<CopyStarted | null>(null)
  const [publishing, setPublishing] = useState<GeneratedAdResponse[] | null>(null)
  const started = useRef(false)

  const load = useCallback(async (brand: number, retry = false) => {
    const state = await AdManagementService.getState(brand, retry)
    setSuggested(state.suggested ?? [])
    setSelected(new Set(state.active_page_ids ?? []))
    setDiscovering(Boolean(state.discovery_in_progress))
    setDiscoveryError(state.discovery_error ?? '')

    const { competitors: tracked } = await AdManagementService.getActiveCompetitors(brand)
    setCompetitors(tracked ?? [])
  }, [])

  useEffect(() => {
    if (!brandId || started.current) return
    started.current = true

    load(brandId)
      .catch((err: unknown) => setError(apiErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [brandId, load])

  const scraping = competitors.filter(isBusy)
  const waiting = discovering || scraping.length > 0 || Boolean(adding)

  useEffect(() => {
    if (!brandId || !waiting) return

    const timer = setInterval(() => {
      void load(brandId).catch(() => undefined)
    }, POLL_INTERVAL)

    return () => clearInterval(timer)
  }, [brandId, waiting, load])

  useEffect(() => {
    const { durationMs } = RESEARCH_STEPS[step]
    if (!durationMs) return

    const timer = setTimeout(() => setStep((current) => current + 1), durationMs)
    return () => clearTimeout(timer)
  }, [step])

  const logos = new Map(competitors.map(({ page_id, profile_picture_url }) => [page_id, profile_picture_url]))
  const ads = mergeAds(competitors, { selected, showInactive, media, favouritesOnly })
  const researching = waiting && ads.length === 0
  const empty = !competitors.length
    ? {
        title: 'No competitors tracked yet',
        body: 'Use the Ad Accounts selector on the top right to add one.',
      }
    : favouritesOnly
      ? {
          title: 'No saved ads yet',
          body: 'Hit the heart icon on any competitor ad to save it here.',
        }
      : !selected.size
        ? {
            title: 'No competitors selected',
            body: 'Use the Ad Accounts selector on the top right to pick whose ads to show.',
          }
        : {
            title: 'No ads found',
            body: 'Check back later, or hit refresh once they publish new ads.',
          }
  const totalAds = competitors
    .filter(({ page_id }) => selected.has(page_id))
    .reduce(
      (sum, { active_scrape, inactive_scrape }) =>
        sum + (active_scrape.total_ads ?? 0) + (showInactive ? (inactive_scrape.total_ads ?? 0) : 0),
      0,
    )

  async function run(action: Promise<unknown>) {
    if (!brandId) return
    try {
      await action
      await load(brandId)
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  function toggleTracked(pageId: string, active: boolean) {
    if (!brandId) return

    setSelected((current) => {
      const next = new Set(current)
      if (active) next.add(pageId)
      else next.delete(pageId)
      return next
    })

    void run(AdManagementService.toggleActiveCompetitor(brandId, pageId, active))
  }

  async function addCompetitor(competitor: BrandLookupMatch | SuggestedCompetitor) {
    if (!brandId) return

    setSelected((current) => new Set(current).add(competitor.page_id))
    setAdding(competitor.name)
    setStep(0)

    try {
      const view = await AdManagementService.addActiveCompetitor(brandId, {
        page_id: competitor.page_id,
        name: competitor.name,
        facebook_page_url: competitor.facebook_page_url,
        profile_picture_url: competitor.profile_picture_url ?? null,
      })

      setCompetitors((current) => [...current.filter(({ page_id }) => page_id !== view.page_id), view])
      posthog.capture('competitor_added')
      if (showInactive) void AdManagementService.scrapeInactive(view.page_id, brandId).catch(() => undefined)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setAdding('')
    }
  }

  async function refreshCompetitor(pageId: string) {
    if (!brandId) return

    setRefreshing((current) => new Set(current).add(pageId))
    await run(AdManagementService.refreshCompetitor(pageId, brandId))
    setRefreshing((current) => {
      const next = new Set(current)
      next.delete(pageId)
      return next
    })
  }

  function toggleInactive() {
    const next = !showInactive
    setShowInactive(next)
    if (!next || !brandId) return

    const missing = competitors.filter(
      ({ page_id, inactive_scrape }) => selected.has(page_id) && !inactive_scrape.last_scraped_at,
    )
    for (const { page_id } of missing) {
      void run(AdManagementService.scrapeInactive(page_id, brandId))
    }
  }

  async function toggleFavourite(ad: CompetitorAd) {
    if (!brandId) return

    setCompetitors((current) =>
      current.map((competitor) => ({
        ...competitor,
        ads: (competitor.ads ?? []).map((item) =>
          item.ad_archive_id === ad.ad_archive_id ? { ...item, is_favourite: !item.is_favourite } : item,
        ),
      })),
    )

    try {
      await AdManagementService.toggleFavouriteAd(brandId, ad.ad_archive_id)
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  async function retryDiscovery() {
    if (!brandId) return

    setDiscoveryError('')
    setDiscovering(true)
    try {
      await load(brandId, true)
    } catch (err) {
      setError(apiErrorMessage(err))
      setDiscovering(false)
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-xl font-semibold text-slate-900">Competitor Research</h1>
          <p className="mt-0.5 text-sm text-slate-500">See what your competitors are running on Meta.</p>
        </div>

        {loading ? (
          <ToolbarSkeleton />
        ) : (
          <div className="flex shrink-0 items-center gap-2 overflow-x-auto [scrollbar-width:thin]">
            <button
              type="button"
              onClick={() => setFavouritesOnly((current) => !current)}
              className={`${CHIP} ${favouritesOnly ? CHIP_ON : CHIP_IDLE}`}
            >
              <HeartIcon className="size-4" />
              Favourites
            </button>

            <Menu>
              <MenuButton className={`${CHIP} ${CHIP_IDLE} whitespace-nowrap`}>
                {MEDIA_LABELS[media]}
                <ChevronDownIcon className="size-3.5 shrink-0 text-gray-400" />
              </MenuButton>
              <MenuItems
                anchor={{ to: 'bottom start', gap: 4 }}
                className="z-50 w-40 rounded-lg border border-gray-200 bg-white py-2 shadow-lg"
              >
                {Object.entries(MEDIA_LABELS).map(([value, label]) => (
                  <MenuItem key={value}>
                    <button
                      type="button"
                      onClick={() => setMedia(value as MediaFilter)}
                      className={`w-full truncate px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${
                        media === value ? 'font-medium text-indigo-600' : 'text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  </MenuItem>
                ))}
              </MenuItems>
            </Menu>

            <button
              type="button"
              disabled={scraping.length > 0}
              onClick={toggleInactive}
              className={`${CHIP} gap-2 ${
                scraping.length > 0
                  ? 'cursor-not-allowed border-gray-200 bg-stone-100 text-gray-400 opacity-60'
                  : showInactive
                    ? CHIP_ON
                    : CHIP_IDLE
              }`}
            >
              <span
                className={`flex h-3.5 w-6 shrink-0 items-center rounded-full px-0.5 transition-colors ${
                  showInactive ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`size-2.5 rounded-full bg-white shadow transition-transform ${
                    showInactive ? 'translate-x-2.5' : ''
                  }`}
                />
              </span>
              Show Inactive Ads
            </button>

            {brandId && (
              <CompetitorsSelector
                brandId={brandId}
                competitors={competitors}
                suggested={suggested}
                activePageIds={selected}
                busy={scraping.length > 0}
                refreshing={refreshing}
                onToggle={toggleTracked}
                onAdd={addCompetitor}
                onRefresh={refreshCompetitor}
              />
            )}
          </div>
      )}
      </div>

      {loading ? (
        <GridSkeleton />
      ) : (
        <>
        {discoveryError && (
          <p className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-800">
            Discovery failed. Please try again or contact support if the issue persists.
            <button type="button" onClick={retryDiscovery} className="font-medium underline">
              Retry
            </button>
          </p>
        )}

        {error && (
          <p role="alert" className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-800">
            {error}
          </p>
        )}

        {researching ? (
          <div className="relative flex min-h-[60vh] items-center justify-center overflow-hidden rounded-2xl border border-slate-100 px-6">
            <div className="pointer-events-none absolute inset-0 bg-linear-to-br from-purple-50/60 via-transparent to-indigo-50/60" />
            <div className="relative flex max-w-lg flex-col items-center gap-5 text-center">
              <span className="size-16 animate-spin rounded-full border-4 border-purple-100 border-t-purple-600" />
              <div className="flex flex-col items-center">
                <p className="text-2xl leading-snug font-semibold text-slate-900">
                  Researching {scraping[0]?.name || adding || 'your competition'}
                </p>
                <p className="flex h-7 items-center text-base leading-relaxed text-slate-500">
                  {RESEARCH_STEPS[Math.min(step, RESEARCH_STEPS.length - 1)].text}
                </p>
              </div>
            </div>
          </div>
        ) : ads.length === 0 ? (
          <div className="flex min-h-[60vh] items-center justify-center rounded-2xl border border-slate-100 px-6">
            <div className="flex max-w-lg flex-col items-center gap-4 text-center">
              <span className="flex size-20 items-center justify-center rounded-full bg-purple-100">
                <MagnifyingGlassIcon className="size-10 text-purple-600" />
              </span>
              <div className="flex flex-col gap-1">
                <p className="text-2xl leading-snug font-semibold text-slate-900">{empty.title}</p>
                <p className="text-base leading-relaxed text-slate-500">{empty.body}</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-end gap-3 px-1">
              <span className="text-xs text-slate-500">
                Total {showInactive ? '' : 'active '}ads: {totalAds}
              </span>
              {(adding || scraping.length > 0) && (
                <span className="flex items-center gap-1.5 text-xs text-indigo-600">
                  <ArrowPathIcon className="size-3.5 shrink-0 animate-spin" />
                  {adding ? `Adding ${adding}` : `Updating ${scraping[0].name}`}
                </span>
              )}
            </div>
            <div className="rounded-2xl border border-slate-100 p-3">
              <div className="columns-1 gap-3 sm:columns-2 lg:columns-3 xl:columns-4">
                {ads.map((ad) => (
                  <CompetitorAdCard
                    key={`${ad.page_id}-${ad.ad_archive_id}`}
                    ad={ad}
                    logo={logos.get(ad.page_id)}
                    onToggleFavourite={toggleFavourite}
                    onClone={(clonedAd, card) => setCloning({ ad: clonedAd, cardIndex: card })}
                  />
                ))}
              </div>
            </div>
        </>
      )}
        </>
      )}

      {cloning && brandId && (
        <CloneAdDialog
          brandId={brandId}
          ad={cloning.ad}
          cardIndex={cloning.cardIndex}
          onClose={() => setCloning(null)}
          onGenerated={(payload) => {
            setCloning(null)
            setReview(payload)
          }}
        />
      )}

      {review && brandId && (
        <AdChatReview
          brandId={brandId}
          ads={review.ads}
          adIndices={review.adIndices}
          creditsRemaining={review.creditsRemaining}
          creditsLimit={review.creditsLimit}
          referenceUrls={review.ads.map(() => review.referenceUrl)}
          onClose={() => setReview(null)}
          onContinue={(updated) => {
            setReview(null)
            setPublishing(updated)
          }}
        />
      )}

      {publishing && brandId && (
        <GeneratedAdFlow
          brandId={brandId}
          ads={publishing}
          onClose={() => setPublishing(null)}
          onPromoted={() => setPublishing(null)}
        />
      )}
    </div>
  )
}
